import { useRequest as useBaseRequest } from 'ahooks'
import type { Service, Options, Result, Response } from './Request'
import { useCallback, useMemo, useState } from 'react'
import type { Options as BaseOptions } from 'ahooks/lib/useRequest/src/types'

export default function useRequest<D extends Response<any> = any, P extends any[] = any[], F = D['data']>(
  service: Service<D, P>,
  options?: Options<D, P, F>
) {
  const [formattedData, setFormattedData] = useState<F>()

  const requestInstance = useBaseRequest<D, P>(service, {
    ...options,
    debounceWait: options?.debounceWait ?? options?.debounceInterval,
    onSuccess(data, params) {
      let res = data?.['data']
      if (options?.formatResult) {
        res = options.formatResult(data)
        setFormattedData(res)
      }
      options?.onSuccess?.(res, params)
    },
  } as BaseOptions<D, P>)

  const data = useMemo(
    () => (options?.formatResult ? formattedData : requestInstance.data?.['data']),
    [requestInstance, options?.formatResult, formattedData]
  )

  const runFn = useCallback(
    async (...params: P) => {
      const resp = await requestInstance.runAsync(...params)
      if (options?.formatResult) {
        return options.formatResult(resp)
      }
      return resp?.['data']
    },
    [requestInstance.runAsync]
  )

  return useMemo(
    () =>
      ({
        ...requestInstance,
        data,
        run: runFn,
        runAsync: runFn,
      }) as Result<D, P, F>,
    [requestInstance, data, runFn]
  )
}
